import {
    domScaleForContainer,
    positionOverlayInReferenceResolution,
} from '../nteOverlayPositioning';
import type { OverlayBounds, Resolution } from '../nteOverlayPositioning';

// Length-valued styles are in reference px and get scaled to screen; the rest copy verbatim.
const SCALED_STYLE_PROPS = [
    'fontSize',
    'lineHeight',
    'letterSpacing',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
];
const COPIED_STYLE_PROPS = [
    'fontFamily',
    'fontWeight',
    'fontStyle',
    'color',
    'textAlign',
    'textTransform',
    'whiteSpace',
];

/** The computed style properties an editable region must supply for its overlay to match the raster. */
export const EDITABLE_OVERLAY_STYLE_PROPS = [...COPIED_STYLE_PROPS, ...SCALED_STYLE_PROPS];

/** A point in viewport coordinates, used to seed the caret from the click that opened editing. */
export interface CaretPoint {
    x: number;
    y: number;
}

export interface EditableRegionDescriptor {
    id: string;
    text: string;
    /** Bounds within the asset's own reference space (0..assetWidth, 0..assetHeight). */
    bounds: OverlayBounds;
    /**
     * How tall (reference px) the region's own text may grow before it's "full" — the space from the
     * region's top down to the bottom of the frame. Per-region, so a full sibling can't block this one.
     */
    maxContentHeight: number;
    style: Record<string, string>;
}

export interface HtmlTextEditConfig {
    /** The asset's box in scene reference coordinates. */
    assetBounds: OverlayBounds;
    referenceResolution: Resolution;
    regions: EditableRegionDescriptor[];
    /** Persist the edited text into `#html` (reloads + re-rasters). Called on blur. */
    commit: (id: string, value: string) => void;
    /** Live-preview the edited text without persisting (no reload). Called on the typing throttle. */
    preview: (id: string, value: string) => void;
}

/** Dispatched (bubbling) on the overlay container when an edit is blocked for overflowing the box. */
export const NTE_TEXT_OVERFLOW_EVENT = 'nte-text-overflow';

function scalePxValue(value: string, scale: number): string {
    const match = /^(-?[\d.]+)px$/.exec(value);
    return match ? `${parseFloat(match[1]) * scale}px` : value;
}

// Each re-raster snapDOMs the composition, so the live type-to-reflow runs on a throttle rather than
// per keystroke (the preview mutates the loaded DOM directly — no iframe reload).
const RE_RASTER_THROTTLE_MS = 50;

interface Overlay {
    id: string;
    element: HTMLElement;
    original: string;
    /** Last text pushed to the live preview, to skip redundant re-rasters on the typing throttle. */
    previewed: string;
    inputTimer: ReturnType<typeof setTimeout> | null;
    lastGoodHtml: string;
    lastGoodText: string;
    /** Reference px the text may occupy before overflowing the frame; refreshed on reposition. */
    maxContentHeight: number;
    overflowing: boolean;
}

/** Screen-px slack absorbing sub-pixel rounding between the overlay and the raster measurement. */
const OVERFLOW_SLACK_PX = 1;

/**
 * Injects a `contentEditable` overlay for the region being edited — the visible copy of the text
 * while the raster hides its glyphs. Edits re-raster the composition (throttled) as you type and
 * commit on blur.
 */
export class HtmlTextEditManager {
    #domContainer: HTMLElement;
    #container: HTMLElement | null = null;
    #overlays: Overlay[] = [];
    #commit: (id: string, value: string) => void;
    #preview: (id: string, value: string) => void;
    #assetSize: Resolution = { width: 0, height: 0 };
    #domScale = 1;

    constructor(domContainer: HTMLElement, config: HtmlTextEditConfig) {
        this.#domContainer = domContainer;
        this.#commit = config.commit;
        this.#preview = config.preview;
        this.#build(config);
    }

    #build(config: HtmlTextEditConfig): void {
        const doc = this.#domContainer.ownerDocument;
        const container = doc.createElement('div');
        container.className = 'l5-html-nte-container';
        container.style.position = 'absolute';
        // The container spans the whole asset but only the active field is interactive; leave the
        // rest transparent so hover/clicks reach the editable-region outlines beneath it.
        container.style.pointerEvents = 'none';
        positionOverlayInReferenceResolution(
            container,
            config.assetBounds,
            config.referenceResolution,
        );
        this.#domContainer.appendChild(container);
        this.#container = container;

        const assetSize: Resolution = {
            width: config.assetBounds.width,
            height: config.assetBounds.height,
        };
        this.#assetSize = assetSize;
        const domScale = domScaleForContainer(this.#domContainer, config.referenceResolution);
        this.#domScale = domScale;

        for (const region of config.regions) {
            const overlay = doc.createElement('div');
            // plaintext-only, not true: it makes Enter insert a single line break instead of Chrome's
            // block-wrapping (`<div><br></div>`), which `innerText` double-counts as two newlines —
            // that doubling accumulated extra <br>s in the composition on every edit round-trip.
            overlay.contentEditable = 'plaintext-only';
            overlay.spellcheck = false;
            overlay.dataset.l5EditableId = region.id;
            overlay.style.position = 'absolute';
            overlay.style.boxSizing = 'border-box';
            overlay.style.outline = 'none';
            // The editor's controls layer sets `user-select: none`; the overlay must opt back in.
            overlay.style.userSelect = 'text';
            overlay.style.setProperty('-webkit-user-select', 'text');
            overlay.style.cursor = 'text';
            overlay.style.pointerEvents = 'auto';
            positionOverlayInReferenceResolution(overlay, region.bounds, assetSize);

            const style = overlay.style as unknown as Record<string, string>;
            for (const prop of COPIED_STYLE_PROPS) {
                style[prop] = region.style[prop] ?? '';
            }
            for (const prop of SCALED_STYLE_PROPS) {
                if (region.style[prop]) {
                    style[prop] = scalePxValue(region.style[prop], domScale);
                }
            }
            this.#setOverlayText(overlay, region.text);

            const entry: Overlay = {
                id: region.id,
                element: overlay,
                original: region.text,
                previewed: region.text,
                inputTimer: null,
                lastGoodHtml: overlay.innerHTML,
                lastGoodText: region.text,
                maxContentHeight: region.maxContentHeight,
                overflowing: false,
            };
            overlay.addEventListener('beforeinput', (event) =>
                this.#handleBeforeInput(entry, event as InputEvent),
            );
            overlay.addEventListener('input', () => this.#handleInput(entry));
            overlay.addEventListener('blur', () => this.#commitOverlay(entry));
            container.appendChild(overlay);
            this.#overlays.push(entry);
        }
    }

    #handleBeforeInput(entry: Overlay, event: InputEvent): void {
        // Once genuinely full, block further inserts (deletions still pass, and clear the flag via the
        // input handler). The flag is only ever set from a stable measurement, so it can't get stuck.
        if (entry.overflowing && event.inputType?.startsWith('insert')) {
            event.preventDefault();
            this.#emitOverflow();
        }
    }

    #handleInput(entry: Overlay): void {
        const text = entry.element.innerText;
        const overflows = this.#overflows(entry);
        // Accept when it fits, or when the edit shortened the text — a reduction must never be
        // reverted, so the user can always delete their way back under the limit. Only a growth that
        // overflows is reverted.
        if (!overflows || text.length <= entry.lastGoodText.length) {
            entry.overflowing = overflows;
            entry.lastGoodHtml = entry.element.innerHTML;
            entry.lastGoodText = text;
            this.#scheduleReRaster(entry);
        } else {
            entry.overflowing = true;
            entry.element.innerHTML = entry.lastGoodHtml;
            this.#placeCaretAtEnd(entry.element);
            this.#emitOverflow();
        }
    }

    // Whether the overlay's text is taller than the region's allotted height. Measured on the overlay
    // itself — always present and never reloaded — so, unlike probing the re-rasterizing iframe, it
    // can't misfire mid-reload. Height auto lets scrollHeight report the natural content height.
    #overflows(entry: Overlay): boolean {
        const element = entry.element;
        const previousHeight = element.style.height;
        element.style.height = 'auto';
        const contentHeight = element.scrollHeight;
        element.style.height = previousHeight;
        return contentHeight > entry.maxContentHeight * this.#domScale + OVERFLOW_SLACK_PX;
    }

    #emitOverflow(): void {
        this.#container?.dispatchEvent(new Event(NTE_TEXT_OVERFLOW_EVENT, { bubbles: true }));
    }

    #placeCaretAtEnd(element: HTMLElement): void {
        const doc = element.ownerDocument;
        const range = doc.createRange();
        range.selectNodeContents(element);
        range.collapse(false);
        const selection = doc.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
    }

    #scheduleReRaster(entry: Overlay): void {
        if (entry.inputTimer !== null) {
            return;
        }
        entry.inputTimer = setTimeout(() => {
            entry.inputTimer = null;
            this.#previewOverlay(entry);
        }, RE_RASTER_THROTTLE_MS);
    }

    // Live-reflow the composition as the user types, without persisting to `#html` (no reload). The
    // final text is committed on blur; until then this just mutates the loaded DOM and re-rasters.
    #previewOverlay(entry: Overlay): void {
        const text = entry.element.innerText;
        if (!entry.id || text === entry.previewed) {
            return;
        }
        entry.previewed = text;
        this.#preview(entry.id, text);
    }

    #commitOverlay(entry: Overlay): void {
        if (entry.inputTimer !== null) {
            clearTimeout(entry.inputTimer);
            entry.inputTimer = null;
        }
        // innerText (not textContent) so hard line breaks come through as `\n`.
        const text = entry.element.innerText;
        if (!entry.id || text === entry.original) {
            return;
        }
        try {
            this.#commit(entry.id, text);
            entry.original = text;
        } catch {
            // The node is no longer targetable in `#html`; drop the edit rather than throw from blur.
        }
    }

    #setOverlayText(element: HTMLElement, text: string): void {
        const doc = element.ownerDocument;
        element.textContent = '';
        text.split('\n').forEach((line, index) => {
            if (index > 0) {
                element.appendChild(doc.createElement('br'));
            }
            element.appendChild(doc.createTextNode(line));
        });
    }

    /**
     * Focus the overlay for a given editable id and place the caret. With a `caretPoint` (viewport
     * coordinates, e.g. from the click that opened editing) the caret lands where the user clicked;
     * otherwise — or if the point misses the field — it falls to the end of the text.
     */
    focus(id: string, caretPoint?: CaretPoint): void {
        const overlay = this.#overlays.find((entry) => entry.id === id);
        if (!overlay) {
            return;
        }
        overlay.element.focus();
        if (!caretPoint || !this.#placeCaretAtPoint(overlay.element, caretPoint)) {
            this.#placeCaretAtEnd(overlay.element);
        }
    }

    #placeCaretAtPoint(element: HTMLElement, point: CaretPoint): boolean {
        const doc = element.ownerDocument as Document & {
            caretPositionFromPoint?(
                x: number,
                y: number,
            ): { offsetNode: Node; offset: number } | null;
            caretRangeFromPoint?(x: number, y: number): Range | null;
        };
        const range = doc.createRange();
        const position = doc.caretPositionFromPoint?.(point.x, point.y);
        if (position && element.contains(position.offsetNode)) {
            range.setStart(position.offsetNode, position.offset);
        } else {
            const fromPoint = doc.caretRangeFromPoint?.(point.x, point.y);
            if (!fromPoint || !element.contains(fromPoint.startContainer)) {
                return false;
            }
            range.setStart(fromPoint.startContainer, fromPoint.startOffset);
        }
        range.collapse(true);
        const selection = doc.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
        return true;
    }

    /** Reposition an overlay to freshly-measured bounds (asset reference space); content and caret untouched. */
    reposition(id: string, bounds: OverlayBounds, maxContentHeight: number): void {
        const overlay = this.#overlays.find((entry) => entry.id === id);
        if (!overlay) {
            return;
        }
        overlay.maxContentHeight = maxContentHeight;
        positionOverlayInReferenceResolution(overlay.element, bounds, this.#assetSize);
    }

    /** Commit any pending (changed) overlay before teardown. */
    commitAll(): void {
        for (const entry of this.#overlays) {
            this.#commitOverlay(entry);
        }
    }

    /** Remove the injected overlay DOM. Kept separate from commitAll so the caller can re-raster first. */
    removeOverlays(): void {
        for (const entry of this.#overlays) {
            if (entry.inputTimer !== null) {
                clearTimeout(entry.inputTimer);
            }
        }
        this.#container?.parentNode?.removeChild(this.#container);
        this.#container = null;
        this.#overlays = [];
    }

    destroy(): void {
        this.commitAll();
        this.removeOverlays();
    }
}
